

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslations } from "next-intl";
import { HoverInfo } from "@/components/HoverInfo/HoverInfo";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import { useSlottingStore, selectTimezone } from "@/lib/slotting/store";
import { updateRequest } from "@/lib/slotting/api";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateOfferingData } from "@/lib/slotting/invalidateSlotting";
import { localTimeToIso } from "@/lib/slotting/utils";
import { IconX, IconPlus, IconTrash, IconCalendar } from "@tabler/icons-react";
import { MultilineInput } from "@/components/Inputs/MultilineInput";
import { OptionInput } from "@/components/Inputs/OptionInput";
import { SliderInput } from "@/components/Inputs/SliderInput";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { DatePicker } from "@/components/DatePicker/DatePicker";
import { useParentPosition } from "@/hooks/useParentPosition";
import { parseDateString, parseToUTCDate } from "@/lib/utils/parser";
import { extractWallParts } from "@/lib/utils/datetime-tz";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { SHEET_INITIAL, SHEET_ANIMATE, SHEET_EXIT, SHEET_SPRING } from "@/components/Modal/sheetMotion";
import type { AppointmentRequest } from "@/lib/slotting/types";
import type { SlottingModalContext } from "@/lib/slotting/modalContext";
import { useCapabilities } from "@/providers/CapabilitiesProvider";
import { useSuppressCoach } from "@/lib/onboarding/useSuppressCoach";
import { useBackdropDismiss } from "@/lib/a11y/useBackdropDismiss";

type AvailabilityRow = {
  date: string;
  startTime: string;
  endTime: string;
};

const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const m = i * 15;
  const h = Math.floor(m / 60).toString().padStart(2, "0");
  const min = (m % 60).toString().padStart(2, "0");
  return `${h}:${min}`;
});

type Props = {
  eventId: string;
  request: AppointmentRequest;
  /** Explicit offering context for callers outside the slotting board (e.g.
   *  the guest-detail popup). When set, store reads are overridden and store
   *  writes are skipped unless the board currently shows the same offering. */
  context?: SlottingModalContext;
  /** Overlay z-class — callers stacked above the board's z ladder raise it. */
  zClass?: string;
  onClose: () => void;
  onSaved?: (updated: AppointmentRequest) => void;
};

function parseAvailabilityRows(
  availability: { startDt: string; endDt: string }[],
  tz: string,
): AvailabilityRow[] {
  if (!availability || availability.length === 0) return [];
  return availability.map((w) => {
    const start = new Date(w.startDt);
    const end = new Date(w.endDt);
    // Format date as YYYY-MM-DD in the offering timezone
    const dateFmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const timeFmt = new Intl.DateTimeFormat("en-GB", {
      timeZone: tz,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return {
      date: dateFmt.format(start),
      startTime: timeFmt.format(start),
      endTime: timeFmt.format(end),
    };
  });
}

export function PSlottingEditRequest({ eventId, request, context, zClass = "z-[80]", onClose, onSaved }: Props) {
  const t = useTranslations("slotting");
  const backdrop = useBackdropDismiss(onClose);
  const queryClient = useQueryClient();
  const tErrors = useTranslations("errors");
  useSuppressCoach(); // hide the bottom-right coach so it can't cover this modal's buttons
  const { can } = useCapabilities();
  const canWrite = can("slotting:write");
  const storeOffering = useSlottingStore((s) => s.offering);
  const storeTreatments = useSlottingStore((s) => s.treatments);
  const updateRequestInStore = useSlottingStore((s) => s.updateRequestInStore);
  const storeTz = useSlottingStore(selectTimezone);

  const offering = context?.offering ?? storeOffering;
  const treatments = context?.treatments ?? storeTreatments;
  const tz = context?.timezone ?? storeTz;

  const offeringId = offering?.offeringId ?? "";

  // In context mode only mirror writes into the board store when the board
  // currently shows this same offering (idempotent live-update); a foreign
  // offering must never leak into the board's state.
  const shouldWriteStore = () =>
    !context || useSlottingStore.getState().offering?.offeringId === offeringId;

  // Form state — pre-populated from request
  const [selectedTreatmentIds, setSelectedTreatmentIds] = useState<string[]>(
    request.treatmentIds ?? [],
  );
  const [availability, setAvailability] = useState<AvailabilityRow[]>(() =>
    parseAvailabilityRows(request.availability ?? [], tz),
  );
  const [notes, setNotes] = useState(request.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attemptedSave, setAttemptedSave] = useState(false);

  const windowErrors = useMemo<Record<number, string | null>>(() => {
    const map: Record<number, string | null> = {};
    availability.forEach((row, i) => {
      const anyFilled = row.date || row.startTime || row.endTime;
      const allFilled = row.date && row.startTime && row.endTime;
      map[i] = anyFilled && !allFilled ? tErrors("slottingWindowIncomplete") : null;
    });
    return map;
  }, [availability, tErrors]);

  const hasNoValidWindow = !availability.some(
    (a) => a.date && a.startTime && a.endTime,
  );
  const hasValidationErrors =
    Object.values(windowErrors).some((e) => e !== null) || hasNoValidWindow;

  // Guest display (read-only)
  const guestDisplay = useMemo(() => {
    const parts = [request.guestFirstName, request.guestLastName].filter(Boolean);
    if (parts.length > 0) return parts.join(" ");
    if (request.guestEmail) return request.guestEmail;
    return t("modalGuestFallback", { id: request.guestId.slice(-6) });
  }, [request, t]);

  const toggleTreatment = useCallback((treatmentId: string) => {
    setSelectedTreatmentIds((prev) =>
      prev.includes(treatmentId)
        ? prev.filter((id) => id !== treatmentId)
        : [...prev, treatmentId],
    );
  }, []);

  const updateAvailability = useCallback(
    (index: number, field: keyof AvailabilityRow, value: string) => {
      setAvailability((prev) =>
        prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
      );
    },
    [],
  );

  const addAvailabilityRow = useCallback(() => {
    const currentDate = context?.currentDate ?? useSlottingStore.getState().currentDate;
    setAvailability((prev) => [
      ...prev,
      { date: currentDate, startTime: "09:00", endTime: "17:00" },
    ]);
  }, [context]);

  const removeAvailabilityRow = useCallback((index: number) => {
    setAvailability((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = useCallback(async () => {
    setError(null);

    if (hasValidationErrors) {
      setAttemptedSave(true);
      return;
    }

    const validWindows = availability.filter(
      (a) => a.date && a.startTime && a.endTime,
    );

    setSaving(true);
    try {
      const updated = await updateRequest(eventId, offeringId, request.appointmentRequestId, {
        treatmentIds: selectedTreatmentIds,
        availability: validWindows.map((a) => ({
          startDt: localTimeToIso(a.date, a.startTime, tz),
          endDt: localTimeToIso(a.date, a.endTime, tz),
        })),
        notes: notes || undefined,
      });
      invalidateOfferingData(queryClient, eventId, offeringId);
      // Merge updated fields back, preserving lookup-derived fields from the original
      const merged = { ...request, ...updated };
      if (!merged.guestFirstName && request.guestFirstName) merged.guestFirstName = request.guestFirstName;
      if (!merged.guestLastName && request.guestLastName) merged.guestLastName = request.guestLastName;
      if (!merged.guestEmail && request.guestEmail) merged.guestEmail = request.guestEmail;
      if (!merged.guestType && request.guestType) merged.guestType = request.guestType;
      if (!merged.companionOf && request.companionOf) merged.companionOf = request.companionOf;
      if (!merged.guestDisplayData && request.guestDisplayData) merged.guestDisplayData = request.guestDisplayData;
      if (!merged.guestAppointments && request.guestAppointments) merged.guestAppointments = request.guestAppointments;
      if (shouldWriteStore()) updateRequestInStore(merged);
      onSaved?.(merged);
      onClose();
    } catch (e) {
      const translated = translateBackendError(tErrors, e instanceof Error ? e : "", t("requestUpdateFailed"));
      setError(translated);
    } finally {
      setSaving(false);
    }
  }, [
    availability,
    selectedTreatmentIds,
    notes,
    queryClient,
    eventId,
    offeringId,
    request,
    updateRequestInStore,
    onSaved,
    onClose,
    t,
    tErrors,
    hasValidationErrors,
  ]);

  return createPortal(
    <div
      className={`fixed inset-0 ${zClass} flex items-end sm:items-center justify-center bg-black/50 sm:pr-[var(--ai-rail-push)] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none`}
      {...backdrop}
    >
      <motion.div
        className="surface-primary rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-lg max-h-[90dvh] overflow-hidden flex flex-col"
        initial={SHEET_INITIAL}
        animate={SHEET_ANIMATE}
        exit={SHEET_EXIT}
        transition={SHEET_SPRING}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Inner scroll container — keeps the scrollbar inside the rounded
            corners so the right-side rounding isn't clipped by the scrollbar. */}
        <div className="overflow-y-auto min-h-0">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b outline-primary">
          <h2 className="h3 font-primary">{t("editSlottingRequest")}</h2>
          <button onClick={onClose} className="font-tertiary hover:font-primary transition-colors">
            <IconX size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Guest (read-only) */}
          <div>
            <label className="block body-2-medium font-secondary mb-1">
              {t("modalGuests")}
            </label>
            <div className="flex items-center px-3 py-2 rounded-lg surface-secondary body-3-regular">
              <span className="font-primary">{guestDisplay}</span>
              {request.guestEmail && guestDisplay !== request.guestEmail && (
                <span className="ml-2 font-tertiary body-4-regular">{request.guestEmail}</span>
              )}
            </div>
          </div>

          {/* Treatments */}
          {treatments.length > 0 && (
            <div>
              <label className="block body-2-medium font-secondary mb-1">
                {t("treatments")}
              </label>
              <div className="space-y-1">
                {treatments.map((tr) => (
                  <SliderInput
                    key={tr.treatmentId}
                    label={`${tr.name}${tr.durationMinutes ? ` (${tr.durationMinutes}min)` : ""}`}
                    checked={selectedTreatmentIds.includes(tr.treatmentId)}
                    setChecked={() => toggleTreatment(tr.treatmentId)}
                    font="body-3-regular font-primary"
                    disabled={saving}
                    hideInfoIcon
                  />
                ))}
              </div>
            </div>
          )}

          {/* Availability windows */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block body-2-medium font-secondary">
                {t("availabilityTitle")}
              </label>
              <button
                onClick={addAvailabilityRow}
                disabled={saving}
                className="inline-flex items-center gap-1 body-4-medium font-secondary hover:font-primary transition-colors disabled:opacity-50"
              >
                <IconPlus size={14} />
                {t("addAvailability")}
              </button>
            </div>
            <div className="space-y-2">
              {availability.length === 0 && (
                <p className="body-4-regular font-tertiary italic">{t("requestsNoAvailability")}</p>
              )}
              {availability.map((row, i) => (
                <AvailabilityRowInput
                  key={i}
                  row={row}
                  index={i}
                  canRemove={true}
                  disabled={saving}
                  onUpdate={updateAvailability}
                  onRemove={removeAvailabilityRow}
                  error={attemptedSave ? windowErrors[i] : null}
                  tz={tz}
                />
              ))}
            </div>
            {attemptedSave && hasNoValidWindow && (
              <p role="alert" className="mt-1 body-3-regular font-error">
                {tErrors("slottingNoWindows")}
              </p>
            )}
          </div>

          {/* Notes */}
          <MultilineInput
            maxLength={5000}
            id="request-notes"
            label={t("notes")}
            value={notes}
            onChangeAction={setNotes}
            rows={2}
            width="w-full"
            height="min-h-16"
            disabled={saving}
            onEnterAction={() => { if (!saving) handleSubmit(); }}
          />

          {/* Error */}
          {error && (
            <div className="rounded-lg border hint-red-outline hint-red-bg hint-red-font px-3 py-2 body-3-medium">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4">
          <SecondaryButton
            text={t("modalClose")}
            onClick={onClose}
            disabled={saving}
            width="w-auto"
          />
          {canWrite && (
            <PrimaryButton
              text={saving ? t("modalSaving") : t("modalSave")}
              onClick={handleSubmit}
              disabled={saving || (attemptedSave && hasValidationErrors)}
              loading={saving}
              width="w-auto"
            />
          )}
        </div>
        </div>
      </motion.div>
    </div>,
    document.body,
  );
}

// --- Availability Row with DatePicker ---

function AvailabilityRowInput({
  row,
  index,
  canRemove,
  disabled,
  onUpdate,
  onRemove,
  error,
  tz,
}: {
  row: AvailabilityRow;
  index: number;
  canRemove: boolean;
  disabled: boolean;
  onUpdate: (index: number, field: keyof AvailabilityRow, value: string) => void;
  onRemove: (index: number) => void;
  error?: string | null;
  /** Resolved event/offering tz from the parent (store- or context-sourced). */
  tz: string;
}) {
  const t = useTranslations("slotting");
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const { dropdownPosition, setDropdownPosition } = useParentPosition();
  const dateRef = useRef<HTMLDivElement>(null);

  // row.date is a pure wall-clock calendar date ("YYYY-MM-DD") — display it
  // verbatim (timeZone "UTC" on its midnight instant), never shifted by the
  // event/browser tz, otherwise it drifts a day across the UTC boundary.
  const formattedDate = parseDateString(parseToUTCDate(row.date), "de-DE", "UTC");

  useEffect(() => {
    if (datePickerOpen && dateRef.current) {
      const updatePosition = () => {
        if (dateRef.current) setDropdownPosition(dateRef);
      };
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [datePickerOpen, setDropdownPosition]);

  const handleOpenDatePicker = () => {
    if (disabled) return;
    if (dateRef.current) setDropdownPosition(dateRef);
    setDatePickerOpen(true);
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
      <div ref={dateRef} className="flex-1">
        <button
          onClick={handleOpenDatePicker}
          disabled={disabled}
          className="truncate overflow-hidden body-2-regular h-9 flex surface-primary w-full rounded-lg px-2 py-2 flex-row font-secondary items-center justify-between border outline-tertiary disabled:opacity-50"
        >
          <div className="flex flex-row items-center gap-2">
            <IconCalendar size={16} />
            <span>{formattedDate || row.date}</span>
          </div>
        </button>
        {datePickerOpen && (
          <DatePicker
            // Anchor at noon UTC so the picker (which reads the wall day in tz)
            // highlights row.date's day for every tz — midnight UTC would land
            // on the previous day west of UTC.
            initialDate={
              row.date ? new Date(`${row.date}T12:00:00Z`) : new Date()
            }
            time={false}
            timeZone={tz}
            closeDatePicker={() => setDatePickerOpen(false)}
            position={dropdownPosition}
            onSelectDate={(date) => {
              // Persist the WALL day picked in the event tz, not the UTC date of
              // the instant (those differ across the UTC boundary → off-by-one).
              const p = extractWallParts(date.toISOString(), tz);
              const isoDate = `${p.year}-${String(p.month + 1).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
              onUpdate(index, "date", isoDate);
              setDatePickerOpen(false);
            }}
          />
        )}
      </div>
      <OptionInput
        inputRef={{ current: null } as React.RefObject<HTMLInputElement | null>}
        options={TIME_OPTIONS}
        defaultValue={row.startTime}
        required={true}
        setParent={(v) => onUpdate(index, "startTime", v)}
        width="w-24"
        height="h-9"
        disabled={disabled}
      />
      <span className="font-tertiary body-4-regular">-</span>
      <OptionInput
        inputRef={{ current: null } as React.RefObject<HTMLInputElement | null>}
        options={TIME_OPTIONS}
        defaultValue={row.endTime}
        required={true}
        setParent={(v) => onUpdate(index, "endTime", v)}
        width="w-24"
        height="h-9"
        disabled={disabled}
      />
      {canRemove && (
        <HoverInfo message={t("removeTimeWindow")} className="inline-flex shrink-0">
          <button
            onClick={() => onRemove(index)}
            disabled={disabled}
            className="font-tertiary hover:font-error transition-colors disabled:opacity-50"
          >
            <IconTrash size={14} />
          </button>
        </HoverInfo>
      )}
      </div>
      {error && (
        <p role="alert" className="body-3-regular font-error">
          {error}
        </p>
      )}
    </div>
  );
}
